import api from '@/api';

/**
 * Actions related to food preparation assignments
 */
export default {
  /**
   * Fetches foods that match a search query
   * @param {Object} context - Vuex context
   * @param {Object} payload - Search parameters
   * @param {string} payload.query - Text to search for
   * @returns {Promise<Array>} - Array of matching foods
   */
  async searchFoods(context, {query = ''} = {}) {
    try {
      const where = {
        meal_food: { some : {} },
        OR: [
          {
            fooddescriptionsimple: {
              contains: query,
              mode: 'insensitive',
            },
          },
          {
            fooddescription: {
              contains: query,
              mode: 'insensitive',
            },
          },
          {
            component: {
              name: {
                contains: query,
                mode: 'insensitive',
              }
            },
          },
        ],
      }
      const queryParam = query ? `?limit=100&where=${encodeURIComponent(JSON.stringify(where))}` : '';
      const {data} = await api.get(`v2/food${queryParam}`);
      return data.rows
        .filter(food => food.meal_food.length > 0)
        .map(food => ({
          id: food.id,
          description: food.fooddescriptionsimple || food.fooddescription,
          ...food
        }));
    } catch (error) {
      console.error('Failed to search foods', error);
      throw error;
    }
  },

  /**
   * Fetches meal food items for a specific food ID
   * @param {Object} context - Vuex context
   * @param {Object} payload - Parameters
   * @param {number|string} payload.foodid - Food ID to fetch meal items for
   * @returns {Promise<Array>} - Array of meal food items
   */
  async fetchFoodMealItems(context, {foodid}) {
    try {
      const {data} = await api.get(`v2/food/${foodid}/meal-food-items`);
      return data.filter(item => !!item.meal_date);
    } catch (error) {
      console.error(`Failed to fetch meal food items for food ID ${foodid}`, error);
      throw error;
    }
  },

  /**
   * Saves a prep step assignment for a meal food item
   * @param {Object} context - Vuex context
   * @param {Object} payload - Assignment details
   * @param {number|string} payload.mealFoodId - Meal food ID
   * @param {string|null} payload.team - Team assignment
   * @param {string} payload.instruction - Prep instruction
   * @param {number|string|null} payload.stepId - Existing step ID (if updating)
   * @returns {Promise<Object>} - The saved step
   */
  async savePrepStep(context, {mealFoodId, team, instruction, stepId}) {
    try {
      const payload = {
        meal_food_id: mealFoodId,
        team,
        instruction: instruction || ''
      };

      let response;
      if (stepId) {
        // Update existing step
        response = await api.put(`v2/meal/steps/update/${stepId}`, payload);
      } else {
        // Create new step
        response = await api.post(`v2/meal/steps`, payload);
      }

      return response.data;
    } catch (error) {
      console.error('Failed to save prep step', error);
      throw error;
    }
  },

  /**
   * Applies a prep assignment to all meal food items with a specific food ID
   * @param {Object} context - Vuex context
   * @param {Object} payload - Assignment details
   * @param {number|string} payload.foodId - Food ID
   * @param {string|null} payload.team - Team assignment
   * @param {string} payload.instruction - Prep instruction
   * @returns {Promise<Object>} - Result of the operation
   */
  async applyPrepToAllFoodItems(context, {foodId, team, instruction}) {
    try {
      const {data} = await api.post(`v2/food/${foodId}/apply-prep`, {
        team,
        instruction
      });
      return data;
    } catch (error) {
      console.error(`Failed to apply prep to all items with food ID ${foodId}`, error);
      throw error;
    }
  },

  /**
   * Applies a team assignment to all meal food items with a specific food ID
   * @param {Object} context - Vuex context
   * @param {Object} payload - Assignment details
   * @param {number|string} payload.foodId - Food ID
   * @param {string} payload.team - Team assignment
   * @returns {Promise<Object>} - Result of the operation
   */
  async applyTeamToAllFoodItems(context, {foodId, team}) {
    try {
      // Apply the team to all items, preserving existing instructions
      const {data} = await api.post(`v2/food/${foodId}/apply-prep`, {
        team,
        instruction: '' // Empty string will keep existing instructions in backend
      });

      return data;
    } catch (error) {
      console.error(`Failed to apply team to all items with food ID ${foodId}`, error);
      throw error;
    }
  }
};